import type { ReactElement, ReactNode } from "react";
import { applyTheme, schriftAus, geraetErmitteln } from "@kundeninfo/ui";
import type { ColorScheme, Density, SchriftEinstellung } from "@kundeninfo/ui";
import { Optionen, Schalter } from "@kundeninfo/ui/me-design";
import "./Einstellungen.css";

export type Preferences = {
  colorScheme: ColorScheme;
  density: Density;
  rememberPanels: boolean;
  fontScale: SchriftEinstellung;
};

/**
 * Die Gruppen der persönlichen Einstellungen (Bereichs-Spalten, Etappe 3).
 *
 * Wie `AKTEN_TABS` in KundenAkte.tsx und `VERWALTUNGS_BEREICHE` in Verwaltung.tsx ist diese
 * Liste die EINE Wahrheit: Der Router liest sie (welche Adressen es gibt,
 * `/einstellungen/<gruppe>`), das Untermenü in der Spalte zeichnet sie
 * (views/EinstellungenSpalte.tsx), und der Schalter unten deckt genau sie ab.
 *
 * Der Zuschnitt ist der BESTAND und keine neue Gliederung: Es sind Zeile für Zeile die drei
 * `fieldset`, die diese Ansicht schon vorher untereinander zeigte. Etappe 3 ist ein reiner
 * Angleich der Optik — was auf dem Bildschirm steht, wandert nur um.
 *
 * „Schriftgröße" kam in ME-Etappe 1 (Aufgabe 5) als vierte Gruppe hinzu — bis dahin war
 * die Schrift eine Systemeinstellung der Administration, keine Benutzerwahl.
 *
 * „Benachrichtigungen" (Tickets Schritt 1b, F109) ist die fünfte Gruppe. Anders als die vier
 * Darstellungsgruppen hat sie einen eigenen Stand am Server (mit Version) und ein eigenes
 * Speichern; ihre Ansicht reicht der Aufrufer über `benachrichtigungen` herein. Der Dialog
 * „Darstellung" zeigt sie nicht.
 */
export const EINSTELLUNGS_GRUPPEN = [
  { id: "farbschema", label: "Farbschema" },
  { id: "dichte", label: "Dichte" },
  { id: "schriftgroesse", label: "Schriftgröße" },
  { id: "randbereiche", label: "Randbereiche" },
  { id: "benachrichtigungen", label: "Benachrichtigungen" },
] as const;

export type EinstellungsGruppe = (typeof EINSTELLUNGS_GRUPPEN)[number]["id"];

export type EinstellungenProps = Preferences & {
  onChange: (p: Preferences) => void;
  /**
   * Welche Gruppe gezeigt wird. FEHLT sie, stehen ALLE untereinander — das ist der Dialog
   * „Darstellung" aus dem Benutzermenü, der unverändert bleibt (Bestandsentscheid: er ist
   * der Zugriff von jedem Bildschirm aus, ohne ihn zu verlassen).
   *
   * Beide Wege zeigen damit weiterhin DIESELBE Komponente mit denselben Werten aus der
   * Sitzung und demselben Speicherweg — der Menüpunkt zeigt nur einen Ausschnitt davon.
   * Eine zweite Komponente für die Bereichs-Fassung hätte zwei Stände zu pflegen, wo es
   * einen gibt.
   */
  gruppe?: EinstellungsGruppe;
  /** Die Ansicht der Gruppe „Benachrichtigungen" (F109) — nur mit `gruppe="benachrichtigungen"`
   *  zu sehen, nie im Dialog „Darstellung". */
  benachrichtigungen?: ReactNode;
};

const FARBSCHEMATA: { value: ColorScheme; label: string }[] = [
  { value: "SYSTEM", label: "System" },
  { value: "LIGHT", label: "Hell" },
  { value: "DARK", label: "Dunkel" },
];

const DICHTEN: { value: Density; label: string }[] = [
  { value: "COMPACT", label: "Kompakt" },
  { value: "COMFORTABLE", label: "Komfortabel" },
];

/**
 * Reihenfolge wie `FONT_SCALES` (packages/domain/src/preferencesRules.ts) — `web` importiert
 * nicht aus `@kundeninfo/domain` (Grenzregel), darum steht die Liste hier noch einmal als
 * Literal. AUTO folgt dem Gerät (siehe `schriftAus` in @kundeninfo/ui): normal am iPhone,
 * sonst klein; eine ausdrückliche Stufe gilt unabhängig vom Gerät.
 */
const SCHRIFTSTUFEN: { value: SchriftEinstellung; label: string }[] = [
  { value: "AUTO", label: "Automatisch (klein am Schreibtisch und iPad, normal am iPhone)" },
  { value: "KLEIN", label: "Klein" },
  { value: "NORMAL", label: "Normal" },
  { value: "GROSS", label: "Groß" },
  { value: "SEHR_GROSS", label: "Sehr groß" },
];

/**
 * Reine Darstellungslogik: Jede Auswahl wendet applyTheme sofort an — die Seite blitzt
 * beim nächsten Aufruf nicht kurz hell auf — und meldet den vollständigen neuen Stand
 * nach außen. Das nebenläufige Speichern am Server (ohne Rückrollen bei einem Fehler)
 * ist Sache des Aufrufers; siehe App.tsx.
 *
 * Farbschema und Dichte reichen bei jeder eigenen Auswahl die AKTUELLE Schriftstufe
 * (über `fontScale`) mit an `applyTheme` durch — sonst risse ein Wechsel von Hell/Dunkel
 * oder Kompakt/Komfortabel `data-schrift` auf die Vorgabe "klein" zurück.
 */
export function Einstellungen(p: EinstellungenProps): ReactElement {
  /** Ohne gewählte Gruppe steht alles da (Dialog), sonst genau die eine (Bereich). */
  function zeigt(gruppe: EinstellungsGruppe): boolean {
    // Die Benachrichtigungen gehören nicht zur Darstellung: nur in ihrer eigenen Gruppe.
    if (gruppe === "benachrichtigungen") return p.gruppe === gruppe;
    return p.gruppe === undefined || p.gruppe === gruppe;
  }

  /** Die aktuelle Schriftstufe für diesen Bildschirm/Zeigertyp — siehe schriftAus(). */
  function aktuelleSchrift(): ReturnType<typeof schriftAus> {
    return schriftAus(p.fontScale, geraetErmitteln());
  }

  function waehleSchema(colorScheme: ColorScheme): void {
    applyTheme(colorScheme, p.density, aktuelleSchrift());
    p.onChange({
      colorScheme, density: p.density, rememberPanels: p.rememberPanels,
      fontScale: p.fontScale,
    });
  }

  function waehleDichte(density: Density): void {
    applyTheme(p.colorScheme, density, aktuelleSchrift());
    p.onChange({
      colorScheme: p.colorScheme, density, rememberPanels: p.rememberPanels,
      fontScale: p.fontScale,
    });
  }

  function wechsleRandbereiche(rememberPanels: boolean): void {
    p.onChange({
      colorScheme: p.colorScheme, density: p.density, rememberPanels,
      fontScale: p.fontScale,
    });
  }

  function waehleSchrift(fontScale: SchriftEinstellung): void {
    applyTheme(p.colorScheme, p.density, schriftAus(fontScale, geraetErmitteln()));
    p.onChange({
      colorScheme: p.colorScheme, density: p.density, rememberPanels: p.rememberPanels,
      fontScale,
    });
  }

  return (
    <div className="es">
      {/* Options-Gruppen als `Optionen` (me-optionen, Entscheid 40, ME-Etappe 4 P17) — bis
          dahin native Radios in `fieldset`/`legend` (Übergang P6-3). Die Werte sind die
          Enum-Namen des Servers; keiner enthält ein Komma. */}
      {zeigt("farbschema") && (
        <div className="es__gruppe">
          <Optionen beschriftung="Farbschema" richtung="waagerecht" wert={p.colorScheme}
            onGeaendert={(d) => waehleSchema(d.wert as ColorScheme)}>
            {FARBSCHEMATA.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </Optionen>
        </div>
      )}

      {zeigt("dichte") && (
        <div className="es__gruppe">
          <Optionen beschriftung="Dichte" richtung="waagerecht" wert={p.density}
            onGeaendert={(d) => waehleDichte(d.wert as Density)}>
            {DICHTEN.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </Optionen>
        </div>
      )}

      {zeigt("schriftgroesse") && (
        <div className="es__gruppe">
          <Optionen beschriftung="Schriftgröße" wert={p.fontScale}
            onGeaendert={(d) => waehleSchrift(d.wert as SchriftEinstellung)}>
            {SCHRIFTSTUFEN.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Optionen>
        </div>
      )}

      {zeigt("randbereiche") && (
        <fieldset className="es__gruppe">
          <legend>Randbereiche</legend>
          {/* Ein Ja/Nein: `Schalter` (me-schalter) statt des Kästchens (ME-Etappe 4, P6). */}
          <Schalter beschriftung="Navigation und Kontext beim nächsten Besuch wie zuletzt anzeigen"
            an={p.rememberPanels} onGeaendert={(d) => wechsleRandbereiche(d.an)} />
        </fieldset>
      )}

      {zeigt("benachrichtigungen") && p.benachrichtigungen}
    </div>
  );
}
